import React, { useState } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  Button,
  Box,
  Alert,
  Divider
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import SendIcon from '@mui/icons-material/Send';

const AutopayDiagnostic = () => {
  const [formData, setFormData] = useState({
    residentName: '',
    propertyAddress: '',
    attemptDate: '',
    attemptTime: '',
    device: '',
    browser: '',
    errorMessage: '',
    failureStep: '',
    paymentMethod: '',
    successMessage: false,
    multipleAttempts: false,
    currentStatus: '',
    additionalNotes: ''
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    try {
      // Send diagnostic data to Firebase Cloud Function
      const response = await fetch('https://us-central1-shelby-estates-hoa.cloudfunctions.net/sendTelegramAlert', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: formatDiagnosticMessage(formData),
          severity: 'high',
          component: 'autopay_diagnostic'
        })
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        throw new Error('Failed to submit diagnostic form');
      }
    } catch (error) {
      console.error('Failed to submit diagnostic:', error);
      alert('Failed to submit form. Please try again or contact admin directly.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDiagnosticMessage = (data) => {
    return `🔍 *Autopay Diagnostic Report*

👤 *Resident:* ${data.residentName}
🏠 *Property:* ${data.propertyAddress}

📅 *When:* ${data.attemptDate} at ${data.attemptTime}
📱 *Device:* ${data.device}
🌐 *Browser:* ${data.browser}

❌ *Error Message:*
${data.errorMessage || 'No error message provided'}

🔄 *Failure Step:* ${data.failureStep}
💳 *Payment Method:* ${data.paymentMethod}

✅ *Success Message First:* ${data.successMessage ? 'Yes' : 'No'}
🔁 *Multiple Attempts:* ${data.multipleAttempts ? 'Yes' : 'No'}

🎯 *Current Status:* ${data.currentStatus}

📝 *Additional Notes:*
${data.additionalNotes || 'None provided'}

---
*Auto-submitted via diagnostic form*`;
  };

  if (submitted) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h5" color="success.main" gutterBottom>
              ✅ Diagnostic Report Submitted
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Thank you! Your diagnostic information has been sent to the admin team.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              We'll review the information and follow up if needed.
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Card>
        <CardContent>
          <Typography variant="h4" gutterBottom align="center">
            🔧 Autopay Issue Diagnostic
          </Typography>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            Having trouble setting up autopay? Please fill out this quick form to help us diagnose the issue.
          </Alert>

          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              
              {/* Contact Info */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  📝 Contact Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Your Name"
                    value={formData.residentName}
                    onChange={handleChange('residentName')}
                    required
                    fullWidth
                  />
                  <TextField
                    label="Property Address"
                    placeholder="e.g., 123 Main St"
                    value={formData.propertyAddress}
                    onChange={handleChange('propertyAddress')}
                    required
                    fullWidth
                  />
                </Box>
              </Box>

              <Divider />

              {/* Timing */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  📅 When Did This Happen?
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Date"
                    type="date"
                    value={formData.attemptDate}
                    onChange={handleChange('attemptDate')}
                    required
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="Approximate Time"
                    placeholder="e.g., 2:30 PM, Morning, etc."
                    value={formData.attemptTime}
                    onChange={handleChange('attemptTime')}
                    fullWidth
                  />
                </Box>
              </Box>

              <Divider />

              {/* Technical Details */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  💻 What Device/Browser?
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControl required>
                    <FormLabel>Device Type</FormLabel>
                    <RadioGroup
                      value={formData.device}
                      onChange={handleChange('device')}
                      row
                    >
                      <FormControlLabel value="phone" control={<Radio />} label="📱 Phone" />
                      <FormControlLabel value="tablet" control={<Radio />} label="📱 Tablet" />
                      <FormControlLabel value="computer" control={<Radio />} label="💻 Computer" />
                    </RadioGroup>
                  </FormControl>

                  <TextField
                    label="Browser"
                    placeholder="e.g., Chrome, Safari, Firefox, Edge"
                    value={formData.browser}
                    onChange={handleChange('browser')}
                    fullWidth
                  />
                </Box>
              </Box>

              <Divider />

              {/* Error Details */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  🚨 Error Details
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Exact Error Message"
                    placeholder="Copy the error message you saw (if any)"
                    value={formData.errorMessage}
                    onChange={handleChange('errorMessage')}
                    multiline
                    rows={3}
                    fullWidth
                  />

                  <FormControl required>
                    <FormLabel>At what step did it fail?</FormLabel>
                    <RadioGroup
                      value={formData.failureStep}
                      onChange={handleChange('failureStep')}
                    >
                      <FormControlLabel value="card-entry" control={<Radio />} label="📝 Entering card information" />
                      <FormControlLabel value="after-submit" control={<Radio />} label="🔄 After clicking Subscribe/Submit" />
                      <FormControlLabel value="processing" control={<Radio />} label="⏳ During payment processing" />
                      <FormControlLabel value="other" control={<Radio />} label="🤔 Something else / Not sure" />
                    </RadioGroup>
                  </FormControl>

                  <FormControl required>
                    <FormLabel>Payment Method</FormLabel>
                    <RadioGroup
                      value={formData.paymentMethod}
                      onChange={handleChange('paymentMethod')}
                    >
                      <FormControlLabel value="new-card" control={<Radio />} label="💳 New credit/debit card" />
                      <FormControlLabel value="saved-card" control={<Radio />} label="💾 Existing saved card" />
                      <FormControlLabel value="bank" control={<Radio />} label="🏦 Bank account (ACH)" />
                      <FormControlLabel value="unsure" control={<Radio />} label="🤔 Not sure" />
                    </RadioGroup>
                  </FormControl>
                </Box>
              </Box>

              <Divider />

              {/* Additional Context */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  🔍 Additional Questions
                </Typography>
                <FormGroup sx={{ gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.successMessage}
                        onChange={handleChange('successMessage')}
                      />
                    }
                    label="✅ Did you see a success message first, then it failed later?"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.multipleAttempts}
                        onChange={handleChange('multipleAttempts')}
                      />
                    }
                    label="🔁 Did you try multiple times?"
                  />
                </FormGroup>

                <TextField
                  label="If you try right now, what happens?"
                  placeholder="e.g., Same error, Works fine, Different error, Haven't tried again"
                  value={formData.currentStatus}
                  onChange={handleChange('currentStatus')}
                  fullWidth
                  sx={{ mt: 2 }}
                />

                <TextField
                  label="Any other details or notes?"
                  placeholder="Anything else that might help us figure this out..."
                  value={formData.additionalNotes}
                  onChange={handleChange('additionalNotes')}
                  multiline
                  rows={3}
                  fullWidth
                  sx={{ mt: 2 }}
                />
              </Box>

              <Divider />

              {/* Submit */}
              <Box sx={{ textAlign: 'center', pt: 2 }}>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  size="large"
                  loading={submitting}
                  loadingPosition="start"
                  startIcon={<SendIcon />}
                  sx={{ minWidth: 200 }}
                >
                  Send Diagnostic Report
                </LoadingButton>
              </Box>

            </Box>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
};

export default AutopayDiagnostic;